import React, { FC, useState } from "react";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Switch } from "@/components/ui/switch";
import {
  CopyPlus,
  CreditCard,
  Edit,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Hotel, Hotel as HotelBrand, HotelAddress } from "@/types/hotel";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { DateCell } from "@/components/pages/settlement/components/table-cells/DateCell";
import { Role } from "@/types/user";
import { SubscriptionPlan } from "@/types/subscription-plan";
import { HotelSubscriptionPlanCell } from "@/components/pages/hotels/components/HotelSubscriptionPlanCell";
import { HotelDeleteDialog } from "@/components/pages/hotels/components/HotelDeleteDialog";
import { HotelDeactivateDialog } from "@/components/pages/hotels/components/HotelDeactivateDialog";

interface HotelsTableProps {
  handleEditBrand: (item: HotelBrand) => void;
  canEditHotels: boolean;
  handleDeleteBrand: (id: string) => Promise<void>;
  canDeleteHotels: boolean;
  isDeletingHotel: boolean;
  handleActivateBrand: (id: string) => Promise<void>;
  handleDeactivateBrand: (id: string) => Promise<void>;
  isChangingHotelStatus: boolean;
  handleSort: (value: string) => void;
  setCurrentPage: (page: number) => void;
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  hotels: HotelBrand[];
  onManageSubscription: (hotel: HotelBrand) => void;
  canManageSubscriptions: boolean;
  tenantId?: string;
  subscriptionPlans?: SubscriptionPlan[];
  handleCopyBrand: (item: HotelBrand) => void;
  /** Copy and Delete are platform actions in tenant mode (DV-168): hidden for others. */
  canCopyHotels: boolean;
  onManageActivityLimit?: (hotel: HotelBrand) => void;
  canManageActivityLimit?: boolean;
}

export const HotelsTable: FC<HotelsTableProps> = ({
  handleEditBrand,
  canEditHotels,
  handleDeleteBrand,
  canDeleteHotels,
  isDeletingHotel,
  handleActivateBrand,
  handleDeactivateBrand,
  isChangingHotelStatus,
  handleSort,
  hotels,
  isLoading,
  sortDirection,
  sortField,
  totalPages,
  currentPage,
  setCurrentPage,
  onManageSubscription,
  canManageSubscriptions,
  tenantId,
  subscriptionPlans = [],
  handleCopyBrand,
  canCopyHotels,
  onManageActivityLimit,
  canManageActivityLimit = false,
}) => {
  const { t } = useTranslation();
  const [hotelToDelete, setHotelToDelete] = useState<HotelBrand | null>(null);
  const [hotelToDeactivate, setHotelToDeactivate] = useState<HotelBrand | null>(
    null,
  );
  const cellTextClassName =
    "text-zinc-900 text-base font-normal tracking-tight";

  const findHotelAdmins = (hotel?: Hotel) => {
    return (
      hotel?.employees?.filter(
        (employee) => employee.role === Role.HOTEL_ADMIN,
      ) ?? []
    );
  };
  const columns: Column<HotelBrand>[] = [
    {
      key: "isActive",
      header: t("hotels.table.status"),
      className: "text-center",
      render: (_, row) => {
        const isActive = (row.isEnabled ?? row.isActive) !== false;
        const statusActionLabel = isActive
          ? t("hotels.table.deactivate")
          : t("hotels.table.activate");
        const statusControlTitle = canEditHotels
          ? statusActionLabel
          : t("common.insufficientPermissions", "Insufficient permissions");

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={isActive}
              disabled={!canEditHotels || isChangingHotelStatus}
              onCheckedChange={(checked) => {
                if (!canEditHotels) {
                  return;
                }

                if (checked) {
                  handleActivateBrand(row.id).catch(() => undefined);
                  return;
                }

                setHotelToDeactivate(row);
              }}
              aria-label={statusActionLabel}
              title={statusControlTitle}
            />
          </div>
        );
      },
      sortable: true,
    },
    ...(canManageSubscriptions
      ? [
          {
            key: "subscription",
            header: t("hotels.table.subscriptionPlan"),
            className: "text-center",
            render: (_: unknown, row: HotelBrand) => (
              <HotelSubscriptionPlanCell
                hotelId={row.id}
                plans={subscriptionPlans}
                canManage={canManageSubscriptions}
                tenantId={tenantId}
              />
            ),
            sortable: false,
          } satisfies Column<HotelBrand>,
        ]
      : []),
    {
      key: "logoId",
      header: t("hotels.table.logo"),
      className: "text-left",
      render: (_, row) => {
        return (
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 rounded object-cover"}
            photo={row.logoId}
            tenantId={row.tenantId}
            placeholder={
              <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
                <span className="text-xs font-medium text-gray-600">
                  {row.name[0].toUpperCase()}
                </span>
              </div>
            }
            alt={row.name}
          />
        );
      },
      sortable: false,
    },
    {
      key: "name",
      header: t("hotels.table.hotelName"),
      className: "text-center",
      render: (value) => {
        return <span className={cellTextClassName}>{value}</span>;
      },
      sortable: true,
    },
    {
      key: "superAdmin",
      header: t("hotels.table.superAdmin"),
      className: "text-center",
      render: (value, row) => {
        const hotelAdmins = findHotelAdmins(row);
        const hotelAdminsCount = hotelAdmins.length;

        const getDisplayText = () => {
          if (hotelAdminsCount === 1) {
            return `${hotelAdmins[0].firstName} ${hotelAdmins[0].lastName}`;
          }
          if (hotelAdminsCount > 1) {
            return `${hotelAdminsCount} ${t("hotels.table.users")}`;
          }
          return "-";
        };

        return <span className={cellTextClassName}>{getDisplayText()}</span>;
      },
    },
    {
      key: "hotelAddress",
      header: t("hotels.table.address"),
      className: "text-center w-[300px]",
      render: (value: HotelAddress) => {
        return (
          <span
            className="line-clamp-2 block max-w-[300px] overflow-hidden text-sm leading-6 text-zinc-900"
            title={value?.fullAddress || "-"}
          >
            {value?.fullAddress || "-"}
          </span>
        );
      },
      sortable: true,
    },
    {
      key: "users",
      header: t("hotels.table.employees"),
      sortable: true,
      className: "text-center",
      render: (_, row) => {
        return (
          <span className={cellTextClassName}>{row.employees?.length}</span>
        );
      },
    },
    {
      key: "rooms",
      header: t("hotels.table.rooms"),
      sortable: true,
      className: "text-center",
      render: (_, row) => {
        return <span className={cellTextClassName}>{row.rooms?.length}</span>;
      },
    },
    {
      key: "pms",
      header: t("hotels.table.pms"),
      className: "text-center",
      render: (value) => {
        return <span className={cellTextClassName}>{value}</span>;
      },
      sortable: true,
    },
    {
      key: "wifiNetwork",
      header: t("hotels.table.wifiNetwork"),
      className: "text-center",
      render: (value) => {
        return <span className={cellTextClassName}>{value}</span>;
      },
      sortable: false,
    },
    {
      key: "createdAt",
      header: t("hotels.table.created"),
      className: "text-center",
      render: (value) => {
        return <DateCell date={value} />;
      },
      sortable: true,
    },
    {
      key: "actions",
      header: t("hotels.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div
          className="flex items-center justify-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onManageSubscription(row)}
            aria-label={t("hotels.table.manageSubscription")}
            title={
              canManageSubscriptions
                ? t("hotels.table.manageSubscription")
                : t(
                    "common.insufficientPermissions",
                    "Insufficient permissions",
                  )
            }
            disabled={!canManageSubscriptions}
          >
            <CreditCard className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleEditBrand(row)}
            title={
              canEditHotels
                ? undefined
                : t(
                    "common.insufficientPermissions",
                    "Insufficient permissions",
                  )
            }
            disabled={!canEditHotels}
          >
            <Edit className="h-4 w-4" />
          </Button>
          {canCopyHotels ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleCopyBrand(row)}
              aria-label={t("hotels.table.copy")}
              title={t("hotels.table.copy")}
            >
              <CopyPlus className="h-4 w-4" />
            </Button>
          ) : null}
          {canManageActivityLimit ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onManageActivityLimit?.(row)}
              aria-label={t("partners.admin.hotelActivityLimit.action")}
              title={t("partners.admin.hotelActivityLimit.action")}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          ) : null}
          {canDeleteHotels ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
              onClick={() => setHotelToDelete(row)}
              aria-label={t("hotels.table.delete")}
              title={t("hotels.table.delete")}
              disabled={isDeletingHotel}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const handleConfirmDelete = async () => {
    if (!hotelToDelete || !handleDeleteBrand) return;

    await handleDeleteBrand(hotelToDelete.id);
    setHotelToDelete(null);
  };

  const handleConfirmDeleteClick = () => {
    handleConfirmDelete().catch(() => undefined);
  };

  const handleConfirmDeactivate = async () => {
    if (!hotelToDeactivate || !canEditHotels) return;

    await handleDeactivateBrand(hotelToDeactivate.id);
    setHotelToDeactivate(null);
  };

  const handleConfirmDeactivateClick = () => {
    handleConfirmDeactivate().catch(() => undefined);
  };

  return (
    <>
      <DataTable
        data={hotels}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
        }}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
        emptyMessage={t("hotels.table.noHotelsFound")}
        onRowClick={canEditHotels ? (row) => handleEditBrand(row) : undefined}
      />
      <HotelDeleteDialog
        hotel={hotelToDelete}
        isPending={isDeletingHotel}
        onOpenChange={(open) => {
          if (!open && !isDeletingHotel) {
            setHotelToDelete(null);
          }
        }}
        onConfirm={handleConfirmDeleteClick}
      />
      <HotelDeactivateDialog
        hotel={hotelToDeactivate}
        isPending={isChangingHotelStatus}
        onOpenChange={(open) => {
          if (!open && !isChangingHotelStatus) {
            setHotelToDeactivate(null);
          }
        }}
        onConfirm={handleConfirmDeactivateClick}
      />
    </>
  );
};
